import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GraduationCap, Search, Video } from 'lucide-react'
import { analyticsService } from '@meiuca/analytics'
import { SCREEN_NAMES } from '@/constants/analyticsEvents'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { MarketplaceFooter } from '@/components/MarketplaceFooter'
import { TrainingCenterCard } from '@/components/TrainingCenterCard'
import { useUserLocation } from '@/hooks/useUserLocation'
import { modalitiesManager } from '@/managers/modalitiesManager'
import { trainingCenterService } from '@/services/trainingCenterService'
import type { Modality } from '@/types/modality'
import { type TrainingCenterListItem } from '@/types/trainingCenter'

export function TrainingCenterSearchPage() {
  const navigate = useNavigate()
  const { userLocation } = useUserLocation()
  const coordinate = userLocation?.coordinate

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedModalities, setSelectedModalities] = useState<Set<string>>(new Set())
  const [remoteOnly, setRemoteOnly] = useState(false)
  const [availableModalities, setAvailableModalities] = useState<Modality[]>([])
  const [centers, setCenters] = useState<TrainingCenterListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.CT_LIST)
  }, [])

  // Catálogo do backend via modalitiesManager, como na busca de arenas.
  // Best-effort: sem catálogo os chips somem, mas a busca continua.
  useEffect(() => {
    modalitiesManager
      .getModalities()
      .then(setAvailableModalities)
      .catch((err) => console.error('[TrainingCenterSearchPage] Error loading modalities:', err))
  }, [])

  const loadCenters = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await trainingCenterService.list({
        q: searchQuery.trim() || undefined,
        // Sem permissão de localização a busca segue funcionando, só não
        // ordena por distância — pedir GPS não pode ser pedágio.
        lat: coordinate?.latitude,
        lng: coordinate?.longitude,
        modalities: selectedModalities.size ? Array.from(selectedModalities) : undefined,
        remote: remoteOnly || undefined
      })
      setCenters(result)
    } catch (err) {
      console.error('[TrainingCenterSearchPage] Error loading centers:', err)
      setError('Não foi possível carregar os centros de treinamento.')
    } finally {
      setLoading(false)
    }
  }, [coordinate?.latitude, coordinate?.longitude, remoteOnly, searchQuery, selectedModalities])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => void loadCenters(), 350)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [loadCenters])

  const toggleModality = (modalityId: string) => {
    setSelectedModalities((current) => {
      const next = new Set(current)
      if (next.has(modalityId)) {
        next.delete(modalityId)
      } else {
        next.add(modalityId)
      }

      analyticsService.track('ct_filter_applied', {
        modality: modalityId,
        remote_only: remoteOnly
      })

      return next
    })
  }

  const toggleRemote = () => {
    const next = !remoteOnly
    setRemoteOnly(next)
    analyticsService.track('ct_filter_applied', {
      modality: Array.from(selectedModalities).join(',') || 'all',
      remote_only: next
    })
  }

  const handleCardClick = (center: TrainingCenterListItem, position: number) => {
    analyticsService.track('ct_card_clicked', {
      ct_id: center.id,
      position,
      is_remote: center.offersRemoteConsulting
    })
    navigate(`/ct/${center.id}`)
  }

  return (
    <div className="min-h-screen bg-background-secondary flex flex-col">
      <MarketplaceHeader onLocationClick={() => {}} />

      <div className="sticky top-16 z-40 bg-background-secondary border-b border-border-light shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Buscar centro de treinamento..."
              className="w-full pl-12 pr-4 py-3 border border-border-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent text-text-primary placeholder:text-text-tertiary"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={toggleRemote}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                remoteOnly
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-background-white text-text-secondary border-border-primary hover:bg-background-tertiary'
              }`}
            >
              <Video className="w-4 h-4" />
              Atende online
            </button>

            {availableModalities.map((option) => {
              const isActive = selectedModalities.has(option.id)
              return (
                <button
                  key={option.id}
                  onClick={() => toggleModality(option.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    isActive
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-background-white text-text-secondary border-border-primary hover:bg-background-tertiary'
                  }`}
                >
                  {option.description}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-text-secondary">{error}</p>
            <button
              onClick={() => void loadCenters()}
              className="mt-4 px-4 py-2 rounded-lg bg-brand-primary text-white font-medium"
            >
              Tentar de novo
            </button>
          </div>
        ) : centers.length === 0 ? (
          <div className="text-center py-20">
            <GraduationCap className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <p className="text-text-primary font-medium">Nenhum centro encontrado</p>
            <p className="text-text-secondary text-sm mt-1">
              Tente outra busca ou remova os filtros.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {centers.map((center, index) => (
              <TrainingCenterCard
                key={center.id}
                center={center}
                onClick={() => handleCardClick(center, index)}
              />
            ))}
          </div>
        )}
      </div>

      <MarketplaceFooter />
    </div>
  )
}
